import * as React from 'react';
import { Checkbox, Input, Modal } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { useDispatch, useSelector } from 'react-redux';

import { sendCustomBenefitEmailRequest } from 'features/benefit/actions/benefitActions';
import {
    getCustomEmailSendSucceeded,
    getIsSendingCustomEmail,
} from 'features/benefit/selectors/benefitSelectors';
import {
    CustomEmailRecipientRole,
    customEmailRecipientRoleLabels,
    customEmailMessageMaxLength,
    customEmailSubjectMaxLength,
} from 'features/benefit/models/benefitModels';

interface SendCustomEmailModalProps {
    visible: boolean;
    onClose: () => void;
    benefitIds: number[];
    // Mirrors the backend rule: tenant administrators may target every group,
    // everyone else may only contact the project managers.
    canTargetAllRoles: boolean;
    onSent?: () => void;
}

const allRecipientRoles = [
    CustomEmailRecipientRole.ContractManager,
    CustomEmailRecipientRole.Assessor,
    CustomEmailRecipientRole.Supplier,
    CustomEmailRecipientRole.DeliveryPartner,
];

export const SendCustomEmailModal: React.FC<SendCustomEmailModalProps> = ({
    visible,
    onClose,
    benefitIds,
    canTargetAllRoles,
    onSent,
}) => {
    const dispatch = useDispatch();
    const isSending = useSelector(getIsSendingCustomEmail);
    const sendSucceeded = useSelector(getCustomEmailSendSucceeded);

    const availableRoles = canTargetAllRoles
        ? allRecipientRoles
        : [CustomEmailRecipientRole.ContractManager];

    const [recipientRoles, setRecipientRoles] = React.useState<CustomEmailRecipientRole[]>([]);
    const [subject, setSubject] = React.useState('');
    const [message, setMessage] = React.useState('');
    const [listBenefits, setListBenefits] = React.useState(true);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);

    React.useEffect(() => {
        if (visible) {
            setRecipientRoles(canTargetAllRoles ? [] : [CustomEmailRecipientRole.ContractManager]);
            setSubject('');
            setMessage('');
            setListBenefits(true);
            setHasSubmitted(false);
        }
    }, [visible, canTargetAllRoles]);

    React.useEffect(() => {
        if (visible && hasSubmitted && !isSending && sendSucceeded) {
            setHasSubmitted(false);
            onSent && onSent();
            onClose();
        }
    }, [visible, hasSubmitted, isSending, sendSucceeded, onSent, onClose]);

    const isValid =
        benefitIds.length > 0 &&
        recipientRoles.length > 0 &&
        subject.trim().length > 0 &&
        message.trim().length > 0;

    const handleSend = () => {
        if (!isValid) {
            return;
        }

        setHasSubmitted(true);
        dispatch(
            sendCustomBenefitEmailRequest({
                benefitIds,
                recipientRoles,
                subject,
                message,
                listBenefits,
            }),
        );
    };

    return (
        <Modal
            title="Send Custom Email"
            visible={visible}
            onCancel={() => !isSending && onClose()}
            onOk={handleSend}
            okText="Send"
            okButtonProps={{ disabled: !isValid, loading: isSending }}
            cancelButtonProps={{ disabled: isSending }}
            closable={!isSending}
            keyboard={!isSending}
            maskClosable={false}
            destroyOnClose
        >
            <p>
                {`The email will be sent in the background to the selected groups on ${
                    benefitIds.length
                } benefit${benefitIds.length === 1 ? '' : 's'}. Each person receives one email.`}
            </p>
            <h4>Recipients:</h4>
            <Checkbox.Group
                options={availableRoles.map((role) => ({
                    label: customEmailRecipientRoleLabels[role],
                    value: role,
                }))}
                value={recipientRoles}
                onChange={(checked: CheckboxValueType[]) =>
                    setRecipientRoles(checked as CustomEmailRecipientRole[])
                }
                disabled={isSending}
            />
            <h4 style={{ marginTop: 16 }}>Subject:</h4>
            <Input
                placeholder="Subject"
                value={subject}
                maxLength={customEmailSubjectMaxLength}
                onChange={(event) =>
                    setSubject(event.target.value.slice(0, customEmailSubjectMaxLength))
                }
                disabled={isSending}
            />
            <h4 style={{ marginTop: 16 }}>Message:</h4>
            <Input.TextArea
                placeholder="Message"
                value={message}
                maxLength={customEmailMessageMaxLength}
                autoSize={{ minRows: 5, maxRows: 12 }}
                onChange={(event) =>
                    setMessage(event.target.value.slice(0, customEmailMessageMaxLength))
                }
                disabled={isSending}
            />
            <Checkbox
                style={{ marginTop: 16 }}
                checked={listBenefits}
                onChange={(event) => setListBenefits(event.target.checked)}
                disabled={isSending}
            >
                Include a list of the selected benefits in the email
            </Checkbox>
        </Modal>
    );
};
